import { IsoDate } from "@figurepos/fgr-lib-business-logic/lib/common/dateTime/types"
import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { parseDate } from "@internationalized/date"
import { addDays, format, parse, subDays } from "date-fns"
import { useEffect } from "react"
import { DateRangePickerValue, RangeCalendarState } from "./types"
import { adjustRangeToFitMaxRange, ISO_DATE_FORMAT } from "./utils"

export const useLimitSelectableDateRange = (
    anchorDateIso: IsoDate | null,
    maxRange: number | null,
    currentMinDate: IsoDate | null,
    currentMaxDate: IsoDate | null,
    propsMinDate: IsoDate | null,
    propsMaxDate: IsoDate | null,
    setCurrentMinDate: (date: IsoDate | null) => void,
    setCurrentMaxDate: (date: IsoDate | null) => void
) => {
    useEffect(() => {
        if (anchorDateIso && maxRange) {
            const anchorDateObj = parse(anchorDateIso, ISO_DATE_FORMAT, new Date())
            const minRangeDate = subDays(anchorDateObj, maxRange - 1)
            const maxRangeDate = addDays(anchorDateObj, maxRange - 1)

            let newMinDate = minRangeDate
            let newMaxDate = maxRangeDate

            const currentMin = currentMinDate ? parse(currentMinDate, ISO_DATE_FORMAT, new Date()) : null
            const currentMax = currentMaxDate ? parse(currentMaxDate, ISO_DATE_FORMAT, new Date()) : null

            if (currentMin) {
                newMinDate = currentMin > minRangeDate ? currentMin : minRangeDate
            }
            if (currentMax) {
                newMaxDate = currentMax < maxRangeDate ? currentMax : maxRangeDate
            }
            setCurrentMinDate(format(newMinDate, ISO_DATE_FORMAT))
            setCurrentMaxDate(format(newMaxDate, ISO_DATE_FORMAT))
        } else {
            // If the anchor is null = no selecting is happening, we should use the original min/max date from props
            setCurrentMinDate(propsMinDate ?? null)
            setCurrentMaxDate(propsMaxDate ?? null)
        }
    }, [
        anchorDateIso,
        maxRange,
        currentMinDate,
        currentMaxDate,
        propsMinDate,
        propsMaxDate,
        setCurrentMinDate,
        setCurrentMaxDate,
    ])
}

export const useAdjustDateOnMaxRangeChange = (
    maxRange: Maybe<number>,
    value: DateRangePickerValue,
    onChange: (value: DateRangePickerValue) => void,
    rangeCalendarState: RangeCalendarState
) => {
    useEffect(() => {
        // Check whether current selected date range is within maxRange.
        // If not, adjust the range to fit maxRange and put the new range startDate to calendar view.
        const adjustedRange = adjustRangeToFitMaxRange(maxRange, value)
        if (adjustedRange !== value && adjustedRange.start && adjustedRange.end) {
            rangeCalendarState.setFocusedDate(parseDate(adjustedRange.start))
            onChange(adjustedRange)
        }
    }, [maxRange, onChange, rangeCalendarState, value])
}
